import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import BrandMark, { LogoFull } from "@/components/BrandMark";
import {
  LayoutDashboard, FolderKanban, Bell, LogOut, Clock, Users as UsersIcon,
  BarChart3, UserCog, UserRound, ChevronDown, Menu, X, CheckCheck, Plane,
  Receipt, Wallet, FileText, KeyRound, CalendarRange, ListTodo, PieChart, Archive,
  ArrowRight, LifeBuoy, Eye, CalendarDays, Upload, ShieldCheck, Sparkles, Inbox,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MobileTabBar } from "@/components/MobileTabBar";

export default function PortalLayout({ children, variant = "client", wide = false }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const avatarSrc = user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : null;
  const location = useLocation();
  const { unread } = useNotifications();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !(p[key] ?? false) }));

  // Păstrează poziția de scroll a sidebar-ului între navigări
  const navScrollRef = useRef(null);
  useLayoutEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem("sidebarScroll");
    if (saved) el.scrollTop = parseInt(saved, 10) || 0;
    const onScroll = () => sessionStorage.setItem("sidebarScroll", String(el.scrollTop));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const clientNav = [
    { to: "/portal/proiecte", label: "Proiecte", icon: FolderKanban },
    { to: "/portal/notificari", label: "Notificări", icon: Bell, badge: unread },
  ];

  const personalItems = [
    { to: "/staff/pontaj", label: "Pontaj", icon: Clock, show: hasPermission("timesheet.self") },
    { to: "/staff/timeoff", label: "Concedii & absențe", icon: Plane, show: true },
    { to: "/staff/decontari", label: "Deconturi", icon: Receipt, show: true },
    { to: "/staff/documente", label: "Documente", icon: FileText, show: true },
    { to: "/staff/cereri", label: "Cereri", icon: Inbox, show: true },
    { to: "/staff/proiecte-acces", label: "Acces proiecte", icon: KeyRound, show: true },
    { to: "/staff/ai", label: "Asistent AI", icon: Sparkles, show: hasPermission("ai.assistant") },
  ];

  const projectItems = [
    { to: "/staff/sarcini", label: "Sarcini", icon: ListTodo, show: hasPermission("projects.workspace") },
    { to: "/staff/planner", label: "Planner resurse", icon: CalendarRange, show: hasPermission("projects.workspace") },
    { to: "/staff/rapoarte-proiect", label: "Rapoarte", icon: FileText, show: hasPermission("projects.workspace") },
  ];

  const approvalsItems = [
    { to: "/staff/aprobari", label: "Aprobări Pontaj", icon: CheckCheck, show: hasPermission("timesheet.approve") },
    { to: "/staff/aprobari-timeoff", label: "Aprobări Concedii & Absențe", icon: Plane, show: hasPermission("timeoff.approve") || hasPermission("hr.manage") || hasPermission("timeoff.records") },
    { to: "/staff/decontari-aprobari", label: "Aprobări Deconturi", icon: Wallet, show: hasPermission("finance.manage") },
    { to: "/staff/cereri-acces-aprobari", label: "Aprobări Acces", icon: KeyRound, show: hasPermission("access.approve") },
  ];

  const organizationItems = [
    { to: "/staff/echipa", label: "Proiecte & Alocări", icon: UsersIcon, show: hasPermission("assignments.manage") },
    { to: "/staff/client", label: "Client", icon: UserRound, show: hasPermission("client.manage") },
    { to: "/staff/rapoarte", label: "Rapoarte financiare", icon: BarChart3, show: hasPermission("reports.view") },
    { to: "/staff/bugete", label: "P&L", icon: PieChart, show: hasPermission("projects.view") && hasPermission("reports.view") },
  ];

  const settingsItems = [
    { to: "/staff/import", label: "Import", icon: Upload, show: hasPermission("settings.manage") },
    { to: "/staff/audit", label: "Audit", icon: ShieldCheck, show: hasPermission("users.manage") },
  ];

  const hrItems = [
    { to: "/staff/utilizatori", label: "Utilizatori", icon: UserCog, show: hasPermission("users.manage") },
    { to: "/staff/timeoff-admin", label: "Concedii & absențe", icon: Plane, show: hasPermission("hr.manage") },
    { label: "Documente", icon: FileText, show: hasPermission("hr.manage"), children: [
      { to: "/staff/documente-admin", label: "Documente angajați" },
      { to: "/staff/documente-colaboratori", label: "Documente colaboratori" },
    ] },
    { to: "/staff/cereri-admin", label: "Cereri angajați", icon: Inbox, show: hasPermission("hr.manage") },
    { to: "/staff/vizualizare-pontaje", label: "Pontaje", icon: Clock, show: hasPermission("timesheet.view_all") },
  ];

  const filterVisible = (items) => items.filter((i) => i.show);

  const staffSections = [
    { key: "top", title: "Navigație principală", items: [{ to: "/staff", label: "Dashboard", icon: LayoutDashboard }] },
    { key: "personal", title: "Personal", items: filterVisible(personalItems) },
    { key: "projects", title: "Proiecte", items: filterVisible(projectItems) },
    { key: "organization", title: "Organizație", items: filterVisible(organizationItems) },
    { key: "approvals", title: "Aprobări", items: filterVisible(approvalsItems) },
    { key: "hr", title: "HR", items: filterVisible(hrItems) },
    { key: "settings", title: "Setări", items: filterVisible(settingsItems) },
  ].filter((s) => s.items.length > 0);

  const clientSections = [{ key: "top", items: clientNav }];
  const sections = variant === "client" ? clientSections : staffSections;

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (variant !== "staff") return;
    document.body.classList.add("pio-theme");
    return () => document.body.classList.remove("pio-theme");
  }, [variant]);

  const renderNavItems = (onClick) => (
    <>
      {sections.map((section) => (
        <div key={section.key}>
          {section.title && <div className="pio-side-label">{section.title}</div>}
          <div className="space-y-0.5">
            {section.items.map((item) => {
              if (item.children) {
                const groupKey = `${section.key}:${item.label}`;
                const GroupIcon = item.icon;
                const childActive = item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
                const isOpen = openGroups[groupKey] ?? childActive;
                return (
                  <div key={groupKey}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupKey)}
                      title={item.label}
                      data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`pio-side-link w-full ${childActive ? "is-active" : ""}`}
                    >
                      <GroupIcon />
                      <span className="flex-1 min-w-0 leading-snug text-left">{item.label}</span>
                      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                        {item.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            onClick={onClick}
                            title={c.label}
                            data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                            className={({ isActive }) => `pio-side-link !py-1.5 ${isActive ? "is-active" : ""}`}
                          >
                            <span className="flex-1 min-w-0 leading-snug text-[13px]">{c.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/staff" || item.to === "/portal/proiecte"}
                  onClick={onClick}
                  title={item.label}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  className={({ isActive }) => `pio-side-link ${isActive ? "is-active" : ""}`}
                >
                  <Icon />
                  <span className="flex-1 min-w-0 leading-snug">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="bg-brand text-white text-[10px] px-1.5 py-0.5 rounded-full tabular-nums">{item.badge}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );

  // Legacy premium layout — client portal only (redesign applies strictly to /staff)
  if (variant === "client") {
    const renderClientNav = (onClick) => (
      <div className="space-y-1">
        {clientNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/portal/proiecte"}
              onClick={onClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `side-link ${isActive ? "is-active" : ""}`}
            >
              <Icon className="h-[18px] w-[18px]" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="bg-brand text-white text-[10px] px-2 py-0.5 rounded-full font-mono">{item.badge}</span>
              )}
            </NavLink>
          );
        })}
      </div>
    );

    return (
      <div className="min-h-screen bg-ink-soft flex">
        <aside data-testid="portal-sidebar" className="w-72 bg-white border-r border-neutral-200 hidden md:flex flex-col">
          <div className="h-24 flex items-center px-6 border-b border-neutral-200">
            <Link to="/portal/proiecte" className="block"><LogoFull variant="dark" size="md" /></Link>
          </div>
          <nav className="flex-1 py-5 px-3 overflow-y-auto">{renderClientNav()}</nav>
          <div className="p-3 border-t border-neutral-200">
            <div className="surface-inset p-4">
              <p className="text-[12px] text-ink-muted leading-relaxed">Pentru asistență, contactează Project Managerul dedicat.</p>
            </div>
          </div>
        </aside>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <aside className="relative w-72 max-w-[80vw] h-full bg-white flex flex-col">
              <div className="h-20 flex items-center justify-between px-6 border-b border-neutral-200">
                <Link to="/portal/proiecte" onClick={() => setMobileOpen(false)}>
                  <LogoFull variant="dark" size="sm" />
                </Link>
                <button data-testid="mobile-close" onClick={() => setMobileOpen(false)} className="h-9 w-9 flex items-center justify-center hover:bg-neutral-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 py-6 px-3 space-y-1">{renderClientNav(() => setMobileOpen(false))}</nav>
            </aside>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-24 bg-white border-b border-neutral-200 flex items-center justify-between px-6 md:px-12">
            <div className="md:hidden flex items-center gap-3">
              <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="h-11 w-11 rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand">
                <Menu className="h-4 w-4" />
              </button>
              <BrandMark variant="dark" className="h-7" />
            </div>
            <div className="hidden md:block">
              <p className="font-display text-[13px] font-medium text-ink-muted mb-1">Portal Client</p>
              <h1 className="font-display font-semibold text-[22px] tracking-[-0.02em] text-ink">Bine ai venit, {user?.name?.split(" ")[0]}</h1>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <button
                data-testid="notif-bell"
                onClick={() => navigate("/portal/notificari")}
                className="relative h-11 w-11 rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand transition-colors"
              >
                <Bell className="h-4 w-4 text-ink" />
                {unread > 0 && (
                  <span data-testid="notif-badge"
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center shadow-sm ring-2 ring-white">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-3 rounded-xl border border-neutral-200 pl-2 pr-3 py-1.5 hover:border-brand transition-colors">
                    <div className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center overflow-hidden">{avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-4 w-4" />}</div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-semibold text-ink leading-tight">{user?.name || "Utilizator"}</div>
                      <div className="text-[11px] text-ink-muted leading-tight">{user?.company || user?.email}</div>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-ink-muted font-normal">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/staff/cont")}>
                    <UserRound className="h-4 w-4 mr-2" /> Contul meu
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
                    className="text-red-600 focus:text-red-600">
                    <LogOut className="h-4 w-4 mr-2" /> Deconectare
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 py-5 px-4 md:py-10 md:px-10 overflow-x-auto">
            <div className="md:min-w-[1120px] max-w-[1440px] mx-auto">{children}</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="pio pio-app-bg min-h-screen flex">
      <aside data-testid="portal-sidebar" className="pio-sidebar w-64 hidden md:flex flex-col sticky top-0 h-screen">
        <div className="pio-sidebar-logo px-3 py-3.5 shrink-0">
          <Link to={variant === "client" ? "/portal/proiecte" : "/staff"} className="pio-logo-panel">
            <img src="/brisk-logo-sidebar.png" alt="Brisk — One Step Ahead" className="w-full max-w-[180px] h-auto" draggable="false" />
          </Link>
        </div>
        <nav ref={navScrollRef} className="pio-side-nav flex-1 py-4 px-3 overflow-y-auto">{renderNavItems()}</nav>
        <div className="px-3 pb-4">
          <div className="pio-help-card" data-testid="sidebar-help-card">
            <div className="flex items-center gap-2.5">
              <span className="pio-help-icon"><LifeBuoy /></span>
              <div className="pio-help-title">Ai nevoie de ajutor?</div>
            </div>
            <p className="pio-help-text">Echipa de suport este aici pentru tine.</p>
            <a href="mailto:suport@briskgrp.com" data-testid="sidebar-support-btn" className="pio-help-btn">
              Contactează suportul <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#060b18]/60 backdrop-blur-[2px] pio-fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="pio-sidebar pio-drawer relative w-[300px] max-w-[85vw] h-full flex flex-col">
            <div className="pio-sidebar-logo px-3 py-3.5 flex items-center gap-2">
              <Link to={variant === "client" ? "/portal/proiecte" : "/staff"} onClick={() => setMobileOpen(false)} className="pio-logo-panel flex-1">
                <img src="/brisk-logo-sidebar.png" alt="Brisk — One Step Ahead" className="w-full max-w-[170px] h-auto" draggable="false" />
              </Link>
              <button data-testid="mobile-close" onClick={() => setMobileOpen(false)} className="h-9 w-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-slate-200 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-3 pt-3">
              <button data-testid="drawer-profile" onClick={() => { setMobileOpen(false); navigate("/staff/cont"); }} className="pio-drawer-profile w-full">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center overflow-hidden shrink-0 ring-2 ring-white/15">
                  {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-[13.5px] font-semibold text-white truncate">{user?.name || "Utilizator"}</div>
                  <div className="text-[11px] text-slate-400 truncate">{user?.job_title || user?.email}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
              </button>
            </div>
            <nav className="pio-side-nav flex-1 py-3 px-3 overflow-y-auto">{renderNavItems(() => setMobileOpen(false))}</nav>
            <div className="px-3 pb-4 pt-2 border-t border-white/10 flex items-center gap-2">
              <a href="mailto:suport@briskgrp.com" data-testid="drawer-support" className="pio-help-btn !mt-0 flex-1">
                <LifeBuoy className="h-3.5 w-3.5" /> Suport
              </a>
              <button data-testid="drawer-logout" onClick={async () => { setMobileOpen(false); await logout(); navigate("/login"); }} className="pio-help-btn !mt-0 flex-1 !border-red-400/40 !text-red-200 hover:!bg-red-500/15">
                <LogOut className="h-3.5 w-3.5" /> Ieșire
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="pio-header h-16 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="md:hidden flex items-center gap-3">
            <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="h-10 w-10 rounded-full border border-slate-200 bg-white/70 flex items-center justify-center hover:border-indigo-400 hover:bg-indigo-50/50 active:scale-95 transition-all">
              <Menu className="h-[18px] w-[18px]" />
            </button>
            <BrandMark variant="dark" className="h-6" />
          </div>
          <div className="hidden md:flex items-center gap-2 text-[13px]">
            <span className="text-slate-400 font-medium">{variant === "client" ? "Portal Client" : "Portal Angajați"}</span>
            <span className="text-slate-300">/</span>
            <span className="font-semibold text-slate-800">Bine ai venit, {user?.name?.split(" ")[0]}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="notif-bell"
              onClick={() => navigate(variant === "client" ? "/portal/notificari" : "/staff/notificari")}
              className="relative h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-indigo-400 hover:bg-indigo-50/50 hover:-translate-y-px transition-all duration-200"
            >
              <Bell className="h-[15px] w-[15px] text-slate-600" />
              {unread > 0 && (
                <span data-testid="notif-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full border border-slate-200 pl-1.5 pr-2.5 h-8 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors">
                  <div className="h-6 w-6 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center overflow-hidden">{avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-3.5 w-3.5" />}</div>
                  <span className="hidden sm:block text-[13px] font-medium text-slate-700 max-w-[160px] truncate">{user?.name || "Utilizator"}</span>
                  <ChevronDown className="h-3 w-3 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/staff/cont")}>
                  <UserRound className="h-4 w-4 mr-2" /> Contul meu
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
                  className="text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Deconectare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 pt-4 px-3 pb-24 sm:pt-6 sm:px-6 md:pb-6 overflow-x-auto">
          <div className={`md:min-w-[1120px] mx-auto ${wide ? "max-w-none" : "max-w-[1440px]"}`}>{children}</div>
        </main>
        <MobileTabBar />
      </div>
    </div>
  );
}
